/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useCommerceApi, useAccessToken} from '@salesforce/commerce-sdk-react';
import {defineMessages, useIntl} from 'react-intl';
import {useLocation} from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {
  RegionModalOtherRegionsArrowIcon,
  RegionPickerRowArrowIcon,
} from '@amblique/king-living/app/components/icons';
import {getSiteConfig} from '@amblique/king-living/app/constants/site-config';
import {useRegionGeoModalCopy} from '@amblique/king-living/app/contentful/hooks/use-region-geo-modal';
import {getRegionPickerSites} from '@amblique/king-living/app/utils/geo-region';
import {replaceRegionModalTokens} from '@amblique/king-living/app/utils/replace-region-modal-tokens';
import {resolveRegionSwitchTargetUrl} from '@amblique/king-living/app/utils/region-switch-url';
import {useOverlayInitialFocus} from '@amblique/king-living/app/hooks/use-overlay-initial-focus';

const REGION_MODAL = {
  bg: '#F7F5F0',
  radius: '4px',

  overlayBg: 'rgba(0, 0, 0, 0.2)',
  overlayBlur: 'blur(16px)',

  textHeading: 'rgba(50, 49, 47, 0.9)',

  textPrimary: '#1C1C1C',

  iconLinen: '#8C683B',
  ink: '#1B1A18',
  inkStroke: 'rgba(27, 26, 24, 0.9)',
  divider: '#E8E8E8',
  linkInk: '#000000',
};

const REGION_PICKER_MAX_ITEMS_PER_COLUMN = 4;

function RegionRedirectCloseGlyph() {
  return (
    <svg
      aria-hidden
      fill="none"
      height="20"
      viewBox="0 0 20 20"
      width="20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g className="region-redirect-close-strokes" opacity={0.3}>
        <path
          d="M0.625 0.625L19.375 19.375"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="1.5"
        />
        <path
          d="M19.375 0.625L0.625 19.375"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="1.5"
        />
      </g>
    </svg>
  );
}

const messages = defineMessages({
  welcome_title_line1: {
    id: 'region_redirect.welcome_title_line1',
    defaultMessage: 'Welcome to',
  },
  welcome_title_line2: {
    id: 'region_redirect.welcome_title_line2',
    defaultMessage: 'King Living {region}',
  },
  welcome_body: {
    id: 'region_redirect.welcome_body',
    defaultMessage:
      "Looks like you're visiting from {detected}.\n\nDo you want to be taken to the {detected} website or continue browsing the {current} website?",
  },
  stay_on_site: {
    id: 'region_redirect.stay_on_site',
    defaultMessage: 'Stay on {region} website',
  },
  go_to_site: {
    id: 'region_redirect.go_to_site',
    defaultMessage: 'Go to {region} website',
  },
  other_regions: {
    id: 'region_redirect.other_regions',
    defaultMessage: 'Other countries/ regions',
  },
  picker_title: {
    id: 'region_redirect.picker_title',
    defaultMessage: 'Select your country/region',
  },
  select_country_assistive: {
    id: 'region_redirect.select_country',
    defaultMessage: 'Select {country}',
  },
  close_modal: {
    id: 'region_redirect.close_modal',
    defaultMessage: 'Close region selection',
  },
});

function RegionPickerGrid({configs, currentSiteId, onPickCurrentSite}) {
  const intl = useIntl();
  const location = useLocation();
  const api = useCommerceApi();
  const {getTokenWhenReady} = useAccessToken();

  const handleSelect = useCallback(
    async (config) => {
      if (!config) return;

      if (config.siteId === currentSiteId) {
        onPickCurrentSite();

        return;
      }

      const url = await resolveRegionSwitchTargetUrl({
        siteConfigEntry: config,
        pathname: location.pathname,
        search: location.search,
        shopperProducts: api.shopperProducts,
        getToken: getTokenWhenReady,
        currentSiteId,
      });

      if (url) {
        window.location.href = url;
      }
    },
    [
      api.shopperProducts,
      currentSiteId,
      getTokenWhenReady,
      location.pathname,
      location.search,
      onPickCurrentSite,
    ]
  );

  const needsTwoColumnLayout = configs.length > REGION_PICKER_MAX_ITEMS_PER_COLUMN;

  return (
    <Grid
      columnGap={needsTwoColumnLayout ? {base: 0, md: 6} : 0}
      rowGap={0}
      width="full"
      templateColumns={
        needsTwoColumnLayout ? {base: '1fr', md: 'repeat(2, 1fr)'} : '1fr'
      }
    >
      {configs.map((config, index) => (
        <Box key={config.siteId} width="full">
          <Button
            _active={{bg: 'blackAlpha.50'}}
            _hover={{bg: 'blackAlpha.50'}}
            aria-label={intl.formatMessage(messages.select_country_assistive, {country: config.country})}
            borderRadius={0}
            color={REGION_MODAL.textPrimary}
            fontSize="16px"
            fontWeight="600"
            height="auto"
            iconSpacing={4}
            justifyContent="space-between"
            letterSpacing="0"
            lineHeight="1.4"
            minH="66px"
            px={0}
            py={4}
            variant="ghost"
            width="full"
            fontFamily="body"
            fontStyle="normal"
            rightIcon={(
              <RegionPickerRowArrowIcon
                color={REGION_MODAL.iconLinen}
                h="20px"
                w="20px"
                aria-hidden
              />
            )}
            onClick={() => handleSelect(config)}
          >
            <Text
              as="span"
              color={REGION_MODAL.textPrimary}
              flex={1}
              fontFamily="body"
              fontSize="16px"
              fontStyle="normal"
              fontVariantNumeric="lining-nums proportional-nums"
              fontWeight="600"
              letterSpacing="0"
              lineHeight="1.4"
              textAlign="left"
            >
              {config.country}
            </Text>
          </Button>
          {index < configs.length - 1 ? <Divider borderColor={REGION_MODAL.divider} /> : null}
        </Box>
      ))}
    </Grid>
  );
}

RegionPickerGrid.propTypes = {
  configs: PropTypes.arrayOf(PropTypes.object).isRequired,
  currentSiteId: PropTypes.string,
  onPickCurrentSite: PropTypes.func.isRequired,
};

export function RegionRedirectModal({
  isOpen,
  suggestion = null,
  currentSiteId,
  onStay,
  onSwitch,
  onClose,
}) {
  const intl = useIntl();
  const closeButtonRef = useRef(null);
  const {data: regionGeoModalCopy} = useRegionGeoModalCopy();
  const [view, setView] = useState('welcome');

  useOverlayInitialFocus({
    initialFocusRef: closeButtonRef,
    isOpen,
  });

  useEffect(() => {
    if (!isOpen) {
      setView('welcome');
    }
  }, [isOpen]);

  const currentConfig = currentSiteId ? getSiteConfig(currentSiteId) : null;
  const currentRegionName = currentConfig?.country || '';

  const detectedRegionName = suggestion?.countryLabel || suggestion?.detectedCountryCode || '';

  const tokenValues = useMemo(
    () => ({
      currentRegion: currentRegionName,
      detectedRegion: detectedRegionName,
    }),
    [currentRegionName, detectedRegionName]
  );

  const {welcomeHeadingFromCms, welcomeBodyText} = useMemo(() => {
    const headingRaw = regionGeoModalCopy?.welcomeHeading;
    const bodyRaw = regionGeoModalCopy?.welcomeBody;

    return {
      welcomeHeadingFromCms: headingRaw ?
        replaceRegionModalTokens(headingRaw, tokenValues) :
        null,
      welcomeBodyText: bodyRaw ?
        replaceRegionModalTokens(bodyRaw, tokenValues) :
        intl.formatMessage(messages.welcome_body, {
          current: currentRegionName,
          detected: detectedRegionName,
        }),
    };
  }, [
    regionGeoModalCopy?.welcomeHeading,
    regionGeoModalCopy?.welcomeBody,
    tokenValues,
    intl,
    currentRegionName,
    detectedRegionName
  ]);

  const welcomeBodyParagraphs = useMemo(() => {
    return welcomeBodyText
      .split(/\n\n+/)
      .map((p) => p.trim())
      .filter(Boolean);
  }, [welcomeBodyText]);

  const handleModalClose = useCallback(() => {
    if (view === 'picker') {
      setView('welcome');

      return;
    }

    onClose();
  }, [view, onClose]);

  const handleOpenPicker = useCallback(() => {
    setView('picker');
  }, []);

  const pickerSites = useMemo(() => getRegionPickerSites(intl.locale), [intl.locale]);

  return (
    <Modal
      initialFocusRef={closeButtonRef}
      isCentered
      isOpen={isOpen}
      scrollBehavior="inside"
      zIndex={2000}
      onClose={handleModalClose}
    >
      <ModalOverlay
        backdropFilter={REGION_MODAL.overlayBlur}
        bg={REGION_MODAL.overlayBg}
        sx={{WebkitBackdropFilter: REGION_MODAL.overlayBlur}}
      />
      <ModalContent
        bg={REGION_MODAL.bg}
        borderRadius={REGION_MODAL.radius}
        boxShadow="lg"
        maxW={{base: '335px', md: '619px'}}
        mx={4}
        p={0}
        position="relative"
        w="full"
      >
        <ModalBody
          alignItems="flex-end"
          display="flex"
          flexDirection="column"
          gap={8}
          overflowY="auto"
          pb={view === 'picker' ? {base: 10, md: 20} : 20}
          pt={6}
          px={8}
        >
          <IconButton
            ref={closeButtonRef}
            aria-label={intl.formatMessage(messages.close_modal)}
            borderRadius={0}
            boxSize="20px"
            color={REGION_MODAL.ink}
            flexShrink={0}
            icon={<RegionRedirectCloseGlyph />}
            minH="20px"
            minW="20px"
            p={0}
            variant="ghost"
            _active={{bg: 'blackAlpha.100'}}
            _focusVisible={{
              boxShadow: '0 0 0 2px rgba(27, 26, 24, 0.35)',
            }}
            _hover={{bg: 'transparent'}}
            sx={{
              h: '20px',
              maxH: '20px',
              minH: '20px !important',
              minW: '20px !important',
              w: '20px',
              '& .region-redirect-close-strokes': {transition: 'opacity 0.15s ease'},
              _hover: {'& .region-redirect-close-strokes': {opacity: 1}},
              _focusVisible: {'& .region-redirect-close-strokes': {opacity: 1}},
            }}
            onClick={handleModalClose}
          />
          {view === 'welcome' ? (
            <>
              <Box
                alignItems="flex-start"
                alignSelf="stretch"
                display="flex"
                flexDirection="column"
                gap={6}
                w="full"
              >
                {welcomeHeadingFromCms ? (
                  <Text
                    as="h2"
                    color={REGION_MODAL.textHeading}
                    fontFamily="heading"
                    fontSize={{base: '24px', md: '32px'}}
                    fontStyle="normal"
                    fontWeight="500"
                    letterSpacing="0"
                    lineHeight={{base: '1.5', md: '1.4'}}
                    m={0}
                    textAlign="left"
                    whiteSpace={{base: 'pre-wrap', md: 'pre-line'}}
                    w="full"
                  >
                    {welcomeHeadingFromCms}
                  </Text>
                ) : (
                  <Text
                    as="h2"
                    color={REGION_MODAL.textHeading}
                    fontFamily="heading"
                    fontSize={{base: '24px', md: '32px'}}
                    fontStyle="normal"
                    fontWeight="500"
                    letterSpacing="0"
                    lineHeight={{base: '1.5', md: '1.4'}}
                    m={0}
                    textAlign="left"
                    whiteSpace={{base: 'pre-wrap', md: 'normal'}}
                    w="full"
                  >
                    <Text as="span" display={{base: 'block', md: 'inline'}}>
                      {intl.formatMessage(messages.welcome_title_line1)}
                    </Text>
                    <Text as="span" display={{base: 'none', md: 'inline'}}>
                      {' '}
                    </Text>
                    <Text as="span" display={{base: 'block', md: 'inline'}}>
                      {intl.formatMessage(messages.welcome_title_line2, {region: currentRegionName})}
                    </Text>
                  </Text>
                )}
                <Box textAlign="left" w="full">
                  {welcomeBodyParagraphs.map((paragraph, index) => (
                    <Text
                      key={index}
                      as="p"
                      color={REGION_MODAL.textHeading}
                      fontFamily="body"
                      fontSize={{base: '14px', md: '16px'}}
                      fontStyle="normal"
                      fontWeight="400"
                      letterSpacing="0"
                      lineHeight="1.4"
                      m={0}
                      mb={index === 0 && welcomeBodyParagraphs.length > 1 ? 4 : 0}
                    >
                      {paragraph}
                    </Text>
                  ))}
                </Box>
              </Box>
              <Box
                alignSelf="stretch"
                display="flex"
                flexDirection="column"
                gap={3}
                w="full"
              >
                <Button
                  borderColor={REGION_MODAL.inkStroke}
                  borderRadius={REGION_MODAL.radius}
                  borderWidth="1px"
                  color={REGION_MODAL.ink}
                  fontSize="16px"
                  fontWeight="600"
                  h="54px"
                  lineHeight="1.4"
                  minH="54px"
                  px={12}
                  py={4}
                  variant="outline"
                  width="full"
                  onClick={onStay}
                >
                  {intl.formatMessage(messages.stay_on_site, {region: currentRegionName})}
                </Button>
                <Button
                  _hover={{bg: REGION_MODAL.ink}}
                  bg={REGION_MODAL.ink}
                  borderColor={REGION_MODAL.ink}
                  borderRadius={REGION_MODAL.radius}
                  borderWidth="1px"
                  color="white"
                  fontSize="16px"
                  fontWeight="600"
                  h="54px"
                  lineHeight="1.4"
                  minH="54px"
                  px={12}
                  py={4}
                  width="full"
                  onClick={onSwitch}
                >
                  {intl.formatMessage(messages.go_to_site, {region: detectedRegionName})}
                </Button>
              </Box>
              <Button
                alignSelf="stretch"
                aria-label={intl.formatMessage(messages.other_regions)}
                color={REGION_MODAL.linkInk}
                fontSize="16px"
                fontWeight="500"
                h="auto"
                iconSpacing={0}
                justifyContent="center"
                lineHeight="1.2"
                minH={0}
                px={0}
                py={0}
                variant="ghost"
                w="full"
                rightIcon={(
                  <RegionModalOtherRegionsArrowIcon
                    aria-hidden
                    color={REGION_MODAL.iconLinen}
                    h="13px"
                    ml="5px"
                    w="14px"
                  />
                )}
                onClick={handleOpenPicker}
              >
                {intl.formatMessage(messages.other_regions)}
              </Button>
            </>
          ) : (
            <Box
              alignSelf="stretch"
              display="flex"
              flexDirection="column"
              gap={{base: 4, md: 6}}
              w="full"
            >
              <Text
                as="h2"
                color={REGION_MODAL.textHeading}
                fontSize={{base: '20px', md: '24px'}}
                fontWeight="500"
                lineHeight={{base: '1.5', md: '1.4'}}
                m={0}
                textAlign="left"
                whiteSpace={{base: 'normal', md: 'nowrap'}}
                w="full"
              >
                {intl.formatMessage(messages.picker_title)}
              </Text>
              <RegionPickerGrid
                configs={pickerSites}
                currentSiteId={currentSiteId}
                onPickCurrentSite={onStay}
              />
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

RegionRedirectModal.propTypes = {
  currentSiteId: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onStay: PropTypes.func.isRequired,
  onSwitch: PropTypes.func.isRequired,
  suggestion: PropTypes.shape({
    countryLabel: PropTypes.string,
    detectedCountryCode: PropTypes.string,
    homeUrl: PropTypes.string,
    targetSiteId: PropTypes.string,
    targetLocale: PropTypes.string,
  }),
};
